/**
 * Biosimilar column badge-button values for oncology submission (same cell type as Sent to Client).
 */

import { TableCellWithBadge } from "../../../data-model/tableCells";

/** API fields used to choose Biosimilar column label and styling. */
interface BiosimilarBadgeButtonInput {
  isBiosimilarCase?: boolean | null;
  parentIdentifier?: string | null;
  sourceDrug?: string | null;
}

/**
 * Returns whether a string field from the API should be treated as present (trimmed, non-empty, not "nan").
 *
 * @param value - Raw string or nullish from the API
 * @returns True when the value is meaningful for parent/source checks
 */
const hasMeaningfulString = (value: string | null | undefined): boolean => {
  if (value == null) {
    return false;
  }
  const trimmed = String(value).trim();
  return trimmed !== "" && trimmed.toLowerCase() !== "nan";
};

/**
 * Builds the badge-button cell value for the Biosimilar column (same component as Sent to Client).
 * When both `parent_identifier` and `source_drug` are set, shows an enabled green "View" control.
 * When the API marks a biosimilar case, shows an enabled black "Retry" control (white text).
 * For all other rows, shows "Retry" with the same black styling but disabled.
 *
 * @param input - Biosimilar-related fields from the order row
 * @returns Value for the badge-button table cell (`BadgeButton` component)
 */
export const getBiosimilarBadgeButtonValue = (
  input: BiosimilarBadgeButtonInput,
): TableCellWithBadge => {
  if (
    hasMeaningfulString(input.parentIdentifier) &&
    hasMeaningfulString(input.sourceDrug)
  ) {
    return {
      text: "View",
      color: "#005D49",
      bgColor: "#E6F3F0",
      isDisabled: false,
    };
  }

  if (input.isBiosimilarCase === true) {
    return {
      text: "Retry",
      color: "#FFFFFF",
      bgColor: "#111111",
      isDisabled: false,
    };
  }

  return {
    text: "Retry",
    color: "#FFFFFF",
    bgColor: "#111111",
    isDisabled: true,
  };
};
